import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Button, CellText, DotsVertical, EmptyState, Menu, Table,
} from '@opendate-app/setlist';

import CrmPageLayout from '../../shared/components/CrmPageLayout';
import CrmSearchInput from '../../shared/components/CrmSearchInput';
import TeamPagination from '../../TeamManager/components/TeamPagination';
import TaskFormModal from './TaskFormModal';

function TasksPage({ team, csrfToken, statuses = [], currentAssigneeTeamMembershipId, customFieldDefinitions = [] }) {
  const isMyTasksView = (() => {
    try {
      const params = new URLSearchParams(window.location.search || '');
      return params.get('view') === 'my';
    } catch (e) {
      return false;
    }
  })();

  const [assignees, setAssignees] = useState([]);
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState(
    isMyTasksView && currentAssigneeTeamMembershipId
      ? String(currentAssigneeTeamMembershipId)
      : ''
  );
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const statusFilterRef = useRef(null);
  const [assigneeFilterOpen, setAssigneeFilterOpen] = useState(false);
  const assigneeFilterRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(25);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  const [taskToRemove, setTaskToRemove] = useState(null);
  const [removeSubmitting, setRemoveSubmitting] = useState(false);
  const [removeError, setRemoveError] = useState(null);

  const [assigneeFilterSearch, setAssigneeFilterSearch] = useState('');
  const [statusFilterSearch, setStatusFilterSearch] = useState('');

  useEffect(() => {
    if (!team || !team.id || !csrfToken) return;

    const fetchDropdownData = async () => {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      try {
        const [assigneesRes, eventsRes] = await Promise.all([
          axios.get(`/teams/${team.id}/crm/tasks/assignees`),
          axios.get(`/teams/${team.id}/crm/tasks/available_events`),
        ]);

        setAssignees(assigneesRes.data.assignees || []);
        setEvents(eventsRes.data.events || []);
      } catch (e) {
        toast.error('Failed to load dropdown data.');
      }
    };

    fetchDropdownData();
  }, [team && team.id, csrfToken]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, assigneeFilter]);

  useEffect(() => {
    if (!team || !team.id) return;

    const fetchTasks = async () => {
      try {
        setLoading(true);
        setError(null);

        if (csrfToken) {
          axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        }

        const params = {
          page: currentPage,
          per_page: perPage,
        };

        if (isMyTasksView) params.view = 'my';

        const q = {};
        if (searchTerm) q.title_or_description_cont = searchTerm;
        if (statusFilter) q.status_eq = statusFilter;
        if (assigneeFilter && !isMyTasksView) q.assignee_team_membership_id_eq = assigneeFilter;
        if (Object.keys(q).length > 0) params.q = q;

        const response = await axios.get(`/teams/${team.id}/crm/tasks.json`, { params });
        const data = response.data || {};
        const tasksFromResponse = data.tasks || [];
        const pagination = data.pagination || {};

        setTasks(tasksFromResponse);
        setTotalEntries(pagination.total_entries || tasksFromResponse.length || 0);
        setTotalPages(pagination.total_pages || 1);
      } catch (e) {
        const raw = e.response && e.response.data;
        let friendlyMessage = 'Failed to load tasks';

        if (Array.isArray(raw)) {
          friendlyMessage = raw.join(', ');
        } else if (typeof raw === 'string' && raw.length < 500 && !raw.trim().startsWith('<')) {
          friendlyMessage = raw;
        }

        setError(friendlyMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, [team && team.id, csrfToken, currentPage, perPage, searchTerm, statusFilter, assigneeFilter]);

  const subtitle = `${totalEntries.toLocaleString()} task${totalEntries === 1 ? '' : 's'}`;

  const openCreateForm = () => {
    setEditingTask(null);
    setShowForm(true);
  };

  // Deep-link support: the CRM dashboard's New task button lands here with
  // ?action=new and expects the create form to open on arrival.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') !== 'new') return;
    openCreateForm();
    params.delete('action');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, []);

  const openEditForm = (task) => {
    setEditingTask(task);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingTask(null);
  };

  const handleTaskSaved = (savedTask, isNew) => {
    if (isNew) {
      setTasks((prev) => [savedTask, ...prev]);
      setTotalEntries((prev) => (prev || 0) + 1);
    } else {
      setTasks((prev) => prev.map((t) => (t.id === savedTask.id ? savedTask : t)));
    }
  };

  useEffect(() => {
    if (!statusFilterOpen) return;

    const handleClick = (event) => {
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) {
        setStatusFilterOpen(false);
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [statusFilterOpen]);

  useEffect(() => {
    if (!assigneeFilterOpen) return;

    const handleClick = (event) => {
      if (assigneeFilterRef.current && !assigneeFilterRef.current.contains(event.target)) {
        setAssigneeFilterOpen(false);
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [assigneeFilterOpen]);

  const closeRemoveModal = () => {
    setTaskToRemove(null);
    setRemoveSubmitting(false);
    setRemoveError(null);
  };

  const handleConfirmRemove = async () => {
    if (!taskToRemove || !team || !team.id) return;

    try {
      setRemoveSubmitting(true);
      setRemoveError(null);
      if (csrfToken) {
        axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      }

      await axios.delete(`/teams/${team.id}/crm/tasks/${taskToRemove.id}.json`);

      setTasks((prev) => prev.filter((t) => t.id !== taskToRemove.id));
      setTotalEntries((prev) => Math.max((prev || 1) - 1, 0));
      closeRemoveModal();
    } catch (err) {
      const resp = err.response;
      if (resp && Array.isArray(resp.data)) {
        setRemoveError(resp.data.join(', '));
      } else {
        setRemoveError('Failed to delete task');
      }
    } finally {
      setRemoveSubmitting(false);
    }
  };

  const renderAssigneeName = (task) => {
    if (!task.assignee_team_membership_id) return 'Unassigned';
    const match = assignees.find((a) => String(a.id) === String(task.assignee_team_membership_id));
    return match ? match.name : 'Unassigned';
  };

  const getStatusBadgeConfig = (status) => {
    const value = status || 'unknown';

    switch (value) {
      case 'open':
        return { label: 'Open', color: '#717680' };
      case 'in_progress':
        return { label: 'In-Progress', color: '#A2DC47' };
      case 'done':
        return { label: 'Done', color: '#12B76A' };
      default:
        return { label: value.replace('_', ' '), color: '#717680' };
    }
  };

  const statusBadgeContainerStyle = {
    background: 'var(--sl-bg-primary)',
    border: '1px solid var(--sl-border-primary)',
    borderRadius: '6px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
  };

  const filterSelectWrapperStyle = {
    position: 'relative',
    minWidth: 160,
  };

  const filterSelectStyle = {
    width: '100%',
    backgroundColor: 'var(--sl-bg-primary)',
    border: '1px solid var(--sl-border-primary)',
    borderRadius: 8,
    padding: '8px 32px 8px 12px',
    boxShadow: '0 1px 2px rgba(10,13,18,0.05)',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    fontSize: 14,
    lineHeight: '20px',
  };

  const filterSelectChevronStyle = {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    color: 'var(--sl-text-quaternary)',
    fontSize: 12,
  };

  const statusMenuStyle = {
    marginTop: 4,
    minWidth: '100%',
    border: '1px solid var(--sl-border-secondary)',
    borderRadius: 8,
    boxShadow:
      '0 12px 16px -4px rgba(10,13,18,0.08), 0 4px 6px -2px rgba(10,13,18,0.03), 0 2px 2px -1px rgba(10,13,18,0.04)',
    padding: '4px 0',
    backgroundColor: 'var(--sl-bg-primary)',
    zIndex: 9999,
  };

  const statusMenuItemStyle = {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '6px 12px',
    border: 'none',
    background: 'transparent',
    textAlign: 'left',
    cursor: 'pointer',
  };

  const statusDotStyle = (color) => ({
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: color,
  });

  const renderStatusBadge = (status) => {
    const { label, color } = getStatusBadgeConfig(status);

    return (
      <div style={statusBadgeContainerStyle}>
        <span style={statusDotStyle(color)} />
        <span className="figma-text secondary xs semibold">{label}</span>
      </div>
    );
  };

  const tableColumns = [
    {
      key: 'task',
      header: 'Task',
      primary: true,
      render: (task) => (
        <CellText
          primary={(
            <Button as="a" href={`/teams/${team.id}/crm/tasks/${task.id}`} variant="link-color">
              {task.title}
            </Button>
          )}
          supporting={task.description ? (
            <span className="d-block text-truncate" style={{ maxWidth: 360 }} title={task.description}>
              {task.description}
            </span>
          ) : undefined}
        />
      ),
    },
    {
      key: 'due',
      header: 'Due',
      render: (task) => (task.due_at ? new Date(task.due_at).toLocaleDateString() : '—'),
    },
    {
      key: 'status',
      header: 'Status',
      render: (task) => renderStatusBadge(task.status),
    },
    {
      key: 'assigned',
      header: 'Assigned',
      render: renderAssigneeName,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '48px',
      render: (task) => (
        <Menu
          placement="bottom-end"
          trigger={(
            <Button
              variant="tertiary"
              size="sm"
              icon={DotsVertical}
              aria-label={`More actions for ${task.title}`}
            />
          )}
          items={[
            { label: 'Edit task', onClick: () => openEditForm(task) },
            { label: 'Delete task', destructive: true, onClick: () => setTaskToRemove(task) },
          ]}
        />
      ),
    },
  ];

  return (
    <CrmPageLayout
      team={team}
      activeSidebarKey={isMyTasksView ? 'my_tasks' : 'all_tasks'}
      mainTopMargin={32}
      title="Tasks"
      subtitle={subtitle}
      primaryAction={{
        label: 'New task',
        iconClass: 'fa fa-plus',
        onClick: openCreateForm,
      }}
    >
      <div className="card">
        <div className="card-body p-0">
          <div className="border-bottom px-4 py-3 d-flex justify-content-between align-items-end crm-toolbar">
            <div className="flex-grow-1 me-3 crm-toolbar-search">
              <CrmSearchInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search tasks"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.preventDefault();
                }}
              />
            </div>

            <div className="d-flex align-items-center crm-toolbar-actions" style={{ gap: '12px' }}>
              <div
                style={filterSelectWrapperStyle}
                ref={assigneeFilterRef}
              >
                <button
                  type="button"
                  style={{
                    ...filterSelectStyle,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingRight: 32,
                  }}
                  onClick={() => setAssigneeFilterOpen((open) => !open)}
                >
                  <span className="figma-text sm">
                    {assigneeFilter
                      ? (assignees.find((a) => String(a.id) === String(assigneeFilter))?.name || 'Assignee')
                      : 'All assignees'}
                  </span>
                  <i className="fal fa-chevron-down" style={filterSelectChevronStyle} aria-hidden="true" />
                </button>

                {assigneeFilterOpen && (
                  <div className="dropdown-menu show" style={statusMenuStyle}>
                    <div style={{ padding: '4px 8px 8px' }}>
                      <CrmSearchInput
                        value={assigneeFilterSearch}
                        onChange={(e) => setAssigneeFilterSearch(e.target.value)}
                        placeholder="Search assignee..."
                        ariaLabel="Search assignee"
                        useDebounce={false}
                        shortcutLabel={null}
                      />
                    </div>
                    <button
                      type="button"
                      style={statusMenuItemStyle}
                      onClick={() => {
                        setAssigneeFilter('');
                        setAssigneeFilterSearch('');
                        setAssigneeFilterOpen(false);
                      }}
                    >
                      <span className="figma-text sm">All assignees</span>
                    </button>
                    {assignees
                      .filter((assignee) => {
                        if (!assigneeFilterSearch) return true;
                        return (assignee.name || '')
                          .toLowerCase()
                          .includes(assigneeFilterSearch.toLowerCase());
                      })
                      .map((assignee) => (
                      <button
                        key={assignee.id}
                        type="button"
                        style={statusMenuItemStyle}
                        onClick={() => {
                          setAssigneeFilter(String(assignee.id));
                          setAssigneeFilterOpen(false);
                        }}
                      >
                        <span className="figma-text sm">{assignee.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div
                style={filterSelectWrapperStyle}
                ref={statusFilterRef}
              >
                <button
                  type="button"
                  style={{
                    ...filterSelectStyle,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingRight: 32,
                  }}
                  onClick={() => setStatusFilterOpen((open) => !open)}
                >
                  <span className="d-inline-flex align-items-center">
                    <span
                      style={{
                        ...statusDotStyle(getStatusBadgeConfig(statusFilter || 'open').color),
                        marginRight: 8,
                      }}
                    />
                    <span className="figma-text sm">
                      {statusFilter
                        ? getStatusBadgeConfig(statusFilter).label
                        : 'All statuses'}
                    </span>
                  </span>
                  <i className="fal fa-chevron-down" style={filterSelectChevronStyle} aria-hidden="true" />
                </button>

                {statusFilterOpen && (
                  <div className="dropdown-menu show" style={{ ...statusMenuStyle, right: 0, left: 'auto' }}>
                    <div style={{ padding: '4px 8px 8px' }}>
                      <CrmSearchInput
                        value={statusFilterSearch}
                        onChange={(e) => setStatusFilterSearch(e.target.value)}
                        placeholder="Search status..."
                        ariaLabel="Search status"
                        useDebounce={false}
                        shortcutLabel={null}
                      />
                    </div>
                    <button
                      type="button"
                      style={statusMenuItemStyle}
                      onClick={() => {
                        setStatusFilter('');
                        setStatusFilterSearch('');
                        setStatusFilterOpen(false);
                      }}
                    >
                      <span
                        style={{
                          ...statusDotStyle('#717680'),
                          marginRight: 8,
                        }}
                      />
                      <span className="figma-text sm">All statuses</span>
                    </button>

                    {statuses
                      .filter((status) => {
                        if (!statusFilterSearch) return true;
                        const { label } = getStatusBadgeConfig(status);
                        return label.toLowerCase().includes(statusFilterSearch.toLowerCase());
                      })
                      .map((status) => {
                      const { label, color } = getStatusBadgeConfig(status);
                      return (
                        <button
                          key={status}
                          type="button"
                          style={statusMenuItemStyle}
                          onClick={() => {
                            setStatusFilter(status);
                            setStatusFilterOpen(false);
                          }}
                        >
                          <span
                            style={{
                              ...statusDotStyle(color),
                              marginRight: 8,
                            }}
                          />
                          <span className="figma-text sm">{label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <Table
            columns={tableColumns}
            data={tasks}
            getRowKey={(task) => task.id}
            loading={loading}
            loadingLabel="Loading tasks..."
            error={error || null}
            empty={<EmptyState size="sm" title="No tasks yet." />}
            stickyFirstColumn
            stickyLastColumn
            stackBelow={640}
          />

          <TeamPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalEntries}
            pageSize={perPage}
            itemLabel="tasks"
            onPageChange={(page) => {
              if (!loading && page >= 1 && page <= totalPages) {
                setCurrentPage(page);
              }
            }}
            itemLabel="tasks"
          />
        </div>
      </div>

      <TaskFormModal
        isOpen={showForm}
        onClose={closeForm}
        task={editingTask}
        team={team}
        csrfToken={csrfToken}
        statuses={statuses}
        assignees={assignees}
        events={events}
        customFieldDefinitions={customFieldDefinitions}
        onSaved={handleTaskSaved}
      />

      {taskToRemove && (
        <div className="team-modal-overlay">
          <div className="team-modal" style={{ maxWidth: 480 }}>
            <div className="team-modal-header">
              <h3 className="team-modal-title">Delete task</h3>
              <button
                type="button"
                className="team-modal-close"
                onClick={closeRemoveModal}
                aria-label="Close"
              >
                <i className="fa fa-times" />
              </button>
            </div>
            <div className="team-modal-body">
              {removeError && (
                <div className="alert alert-danger py-2 mb-2">{removeError}</div>
              )}
              <p className="mb-0">Are you sure? This cannot be undone.</p>
            </div>
            <div className="team-modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={closeRemoveModal}
                disabled={removeSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleConfirmRemove}
                disabled={removeSubmitting}
              >
                {removeSubmitting ? 'Deleting…' : 'Delete task'}
              </button>
            </div>
          </div>
        </div>
      )}
    </CrmPageLayout>
  );
}

export default TasksPage;
